import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate, formatTime, useTimeFormat } from "helpers/timeFormat";
import { useSearchParams } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { handlePaginateNotes, createNote } from "store/players/list/slice";
import { playersListLimits } from "configs/limits";

import Pagination from "components/Pagination";
import Input from "components/inputs/InnerInput/InnerInput";
import Button from "components/buttons/Button";
import styles from "./ListNotesTab.module.scss";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { handleApiError } from "helpers/handleApiError";

interface Props {
  limit: number;
  onLimitChange: (limit: number) => void;
}

const ListNotesTab: React.FC<Props> = ({ limit, onLimitChange }) => {
  const timeFormat = useTimeFormat();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const notesList = useAppSelector((store) => store.players.notesList);
  const totalItems = useAppSelector((store) => store.players.totalItemsNotes);
  const totalPages = useAppSelector((store) => store.players.totalPagesNotes);
  const activePage = useAppSelector((store) => store.players.activePageNotes);
  const [value, setValue] = useState<string>("");
  const permissions = usePermissionHelper();
  const canCreate = permissions?.create ?? false;

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginateNotes(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginateNotes(activePage + 1));
    }
  };

  const handleCreate = async () => {
    try {
      await dispatch(
        createNote({
          content: value,
          userId: parseInt(searchParams.get("entityId") ?? "0", 10),
        }),
      ).unwrap();
      setValue("");
    } catch (error) {
      handleApiError({ error, t, dispatch });
    }
  };

  return (
    <div className={`${styles.notes} player-content-wrapper`}>
      {canCreate && (
        <div className="create-note-wrap">
          <div className="title">{t("addNotice")}</div>
          <div className="row">
            <Input value={value} onChange={setValue} type="text" id="note" />
            <Button isSecondary txt={t("save")} handleClick={handleCreate} />
          </div>
        </div>
      )}
      <div className={`${styles.table} inner-table-wrapper notes-table`}>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>{t("content")}</th>
              <th>{t("created")}</th>
            </tr>
          </thead>
          <tbody>
            {notesList.map((el) => (
              <tr key={el.id}>
                <td>{el.id}</td>
                <td className="big-text">{el.content}</td>
                <td className="date">
                  <span>{formatDate(el.created)}</span>
                  <span>{formatTime(el.created, timeFormat)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        activePage={activePage}
        limit={limit}
        totalItems={totalItems}
        totalPages={totalPages}
        limitsArray={playersListLimits}
        handleLimitClick={(nextLimit) => {
          onLimitChange(nextLimit);
          dispatch(handlePaginateNotes(1));
        }}
        handleNextClick={handleNextClick}
        handlePrevClick={handlePrevClick}
      />
    </div>
  );
};

export default ListNotesTab;

// NotesTable
